import React from 'react';
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import Colours from '@shared/ui/atoms/Colours';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { HeatGrid } from "./heatMapData";
import { binStyle, formatPercent, heatBin } from "./heatScale";
import { HeatTooltip, useHeatTooltip } from "./HeatTooltip";

// The FlatContentCard the grid sits on. The sticky floor column and the gaps
// between weeks are painted in it.
const SURFACE = Colours.veryLightGrey;

interface Props {
  grid: HeatGrid;
  selectedFloorId?: number;
  onFloorSelected: (floorId: number) => void;
  // Print the percentage in each square. Only when the squares are wide enough.
  showValues: boolean;
}

export function OccupancyHeatGrid(props: Props) {
  const {grid, selectedFloorId, onFloorSelected, showValues} = props;
  const {t} = useTranslation();
  const {tip, markProps} = useHeatTooltip();

  return (
    <>
      <ScrollArea>
        <Table>
          <thead>
            <tr>
              <FloorHeader scope="col">{t('reports-page.office-usage.heat-map.floor')}</FloorHeader>
              {grid.columns.map(column => (
                <ColumnHeader key={column.key} scope="col" $startsGroup={column.startsGroup}>
                  <span>{column.heading}</span>
                  <span>{column.subheading}</span>
                </ColumnHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.rows.map(row => {
              const selected = row.floorId === selectedFloorId;
              return (
                <tr key={row.floorId}>
                  <FloorCell scope="row">
                    <FloorButton type="button" $selected={selected} aria-pressed={selected} onClick={() => onFloorSelected(row.floorId)}>
                      <FloorText>
                        <span>{row.label}</span>
                        <FloorCapacity>{t('reports-page.office-usage.heat-map.capacity', {capacity: row.capacity})}</FloorCapacity>
                      </FloorText>
                      <Icon icon={IconTypes.Next} size="verySmall" color={selected ? 'blueIcon' : 'grey'} />
                    </FloorButton>
                  </FloorCell>
                  {row.cells.map((cell, index) => {
                    const bin = heatBin(cell.ratio);
                    const column = grid.columns[index];
                    return (
                      <HeatCell key={column.key}
                                $startsGroup={column.startsGroup}
                                $selected={selected}
                                {...markProps(cell.description, {title: row.label, subtitle: cell.when, lines: cell.lines, bin})}
                                onClick={() => onFloorSelected(row.floorId)}
                                style={binStyle(bin)}>
                        {showValues && cell.ratio !== undefined && cell.ratio > 0 ? formatPercent(cell.ratio) : ''}
                      </HeatCell>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </ScrollArea>
      <HeatTooltip tip={tip} />
    </>
  );
}

const ScrollArea = styled.div`
  overflow-x: auto;
  padding-bottom: 8px;
`

// The 2px spacing is the gap between squares
const Table = styled.table`
  border-collapse: separate;
  border-spacing: 2px;
  font-family: "soleil", sans-serif;
  /* Undo the app-wide table styles in _scroll-table.scss (striped rows, white header cells) */
  && tr {
    background-color: transparent;
  }
  && th {
    background-color: ${SURFACE};
  }
`

const FloorHeader = styled.th`
  position: sticky;
  left: 0;
  z-index: 1;
  background: ${SURFACE};
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: ${Colours.darkGrey};
  padding: 4px 12px 4px 0;
  vertical-align: bottom;
`

const ColumnHeader = styled.th<{ $startsGroup: boolean }>`
  font-size: 11px;
  font-weight: 400;
  color: ${Colours.darkGrey};
  padding: 0 0 4px 0;
  min-width: 32px;
  text-align: center;
  white-space: nowrap;
  vertical-align: bottom;
  border-left: ${p => p.$startsGroup ? `6px solid ${SURFACE}` : 'none'};
  span {
    display: block;
  }
  span:first-child {
    font-weight: 600;
    color: ${Colours.black};
  }
`

const FloorCell = styled.th`
  position: sticky;
  left: 0;
  z-index: 1;
  background: ${SURFACE};
  padding: 0 8px 0 0;
  font-weight: 400;
`

// Reads as a button at rest, not only on hover, so the floors look clickable
const FloorButton = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-width: 140px;
  max-width: 240px;
  background: ${p => p.$selected ? Colours.blue10 : Colours.white};
  border: 1px solid ${p => p.$selected ? Colours.blue : Colours.mildGrey};
  border-radius: 6px;
  padding: 4px 4px 4px 8px;
  cursor: pointer;
  text-align: left;
  font-family: "soleil", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${Colours.black};
  &:hover {
    border-color: ${Colours.blue};
  }
`

const FloorText = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
`

const FloorCapacity = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: ${Colours.darkGrey};
`

const HeatCell = styled.td<{ $startsGroup: boolean; $selected: boolean }>`
  min-width: 32px;
  height: 36px;
  padding: 0 4px;
  border-radius: 4px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  /* A wider gap before each new week or weekday */
  border-left: ${p => p.$startsGroup ? `6px solid ${SURFACE}` : 'none'};
  /* Black rather than blue, which would vanish on the middle of the scale */
  outline: ${p => p.$selected ? `2px solid ${Colours.black}` : 'none'};
  outline-offset: -2px;
  &:focus-visible {
    outline: 2px solid ${Colours.black};
    outline-offset: 1px;
  }
`
